import {type ReactNode, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  getDisplayedFinancialProductQuote,
  getFinancialProductOffer,
  toFinancialProductPreferenceCoordinates,
} from 'features/financial-products';

import {
  FinancialProductOffer,
  FinancialProductPreferenceCoordinates,
  FinancialProductType,
} from '../../api/types/onlineOfferTypes';
import {FinancialProductPreferenceContext} from './FinancialProductPreferenceContext';
import {useFinancialProductPreference} from './useFinancialProductPreference';

interface FinancialProductPreferenceProviderProps {
  children: ReactNode;
}

/** A signed snapshot shows exactly what was stored, so it is described by its stored selection. */
const toStoredCoordinates = (
  offer: FinancialProductOffer
): FinancialProductPreferenceCoordinates =>
  isNil(offer.selection)
    ? toFinancialProductPreferenceCoordinates(offer)
    : {...offer.selection, productType: offer.productType};

/**
 * Owns the customer's financing choice for everything on the page that shows it. A slider being
 * dragged is only previewed: the result follows it locally and nothing is written until release.
 * Inputs always come from the authoritative body, so an answer for an older choice re-prices the
 * shown coordinates but never replaces a newer draft or preview.
 */
export function FinancialProductPreferenceProvider(props: FinancialProductPreferenceProviderProps) {
  const preference = useFinancialProductPreference();
  const [previewCoordinates, setPreviewCoordinates] =
    useState<FinancialProductPreferenceCoordinates | null>(null);
  const {products, opened, isReadOnly} = preference;

  // A contract signed mid-drag removes the slider before its release could clear the preview.
  if (isReadOnly && isNotNil(previewCoordinates)) {
    setPreviewCoordinates(null);
  }

  const toOfferCoordinates = (offer: FinancialProductOffer) =>
    isReadOnly ? toStoredCoordinates(offer) : toFinancialProductPreferenceCoordinates(offer);

  const openedCoordinates =
    isNotNil(opened) && isReadOnly ? toOfferCoordinates(opened.offer) : opened?.coordinates;
  const displayedCoordinates =
    !isReadOnly && previewCoordinates?.productType === opened?.offer.productType
      ? previewCoordinates
      : (openedCoordinates ?? null);

  const getDisplayed = (productType: FinancialProductType) => {
    const offer = isNil(products) ? null : getFinancialProductOffer(products, productType);

    if (isNil(products) || isNil(offer)) {
      return {source: 'NONE' as const};
    }

    const coordinates =
      productType === opened?.offer.productType
        ? (displayedCoordinates ?? toOfferCoordinates(offer))
        : toOfferCoordinates(offer);

    return getDisplayedFinancialProductQuote({envelope: products, offer, coordinates});
  };

  const commit = (coordinates: FinancialProductPreferenceCoordinates) => {
    setPreviewCoordinates(null);
    preference.commit(coordinates);
  };

  const openProduct = (productType: FinancialProductType) => {
    setPreviewCoordinates(null);
    preference.openProduct(productType);
  };

  return (
    <FinancialProductPreferenceContext.Provider
      value={{
        ...preference,
        commit,
        openProduct,
        displayedCoordinates,
        preview: setPreviewCoordinates,
        getDisplayed,
      }}
    >
      {props.children}
    </FinancialProductPreferenceContext.Provider>
  );
}
